/**
 * The join lifecycle — from a stranger's request to an active member.
 *
 *   pending ──(admin approves & sends link)──▶ link_sent
 *   link_sent ──(member pays on the link)────▶ paid
 *   paid ──(member sets details + password)──▶ paid + onboarded, link USED
 *   paid ──(admin activates)─────────────────▶ approved  → member exists,
 *                                                          can sign in
 *   any  ──(admin declines)──────────────────▶ declined
 *
 * Rules the committee cares about, enforced here and tested in join.test.js:
 *   - A payment link is ONE-TIME. It dies when onboarding completes, and
 *     issuing a fresh link voids every earlier one for that request.
 *   - Paying twice on the same link is impossible.
 *   - A member record does not exist until the admin's FINAL approval — so
 *     nobody can sign in early. The bib is allocated at that moment.
 *   - The membership clock starts from the day they paid, not the day the
 *     admin got around to activating them.
 *
 * These are pure draft-mutators: they take the cloned db that ClubContext's
 * `commit` hands out, plus any ids the caller allocated. Against the real
 * backend the same logic runs inside API transactions.
 */

import { TIERS } from './constants';
import { nextPaidTill } from './dues';
import { normalizeEmail } from './email';
import { findMemberByPhone, normalizePhone } from './phone';
import { today } from './time';

/** Request statuses that mean "still in the pipeline, not yet a member". */
export const JOIN_IN_PROGRESS = ['pending', 'link_sent', 'paid'];

export const PASSWORD_MIN = 6;

/** Why a password is unacceptable, or null if it is fine. */
export function passwordProblem(pw) {
  if (!pw || pw.length < PASSWORD_MIN) {
    return `Password must be at least ${PASSWORD_MIN} characters.`;
  }
  if (!/[0-9]/.test(pw)) return 'Password must include at least one number.';
  return null;
}

/** The link and request behind a token, or nulls. */
export function joinRequestByToken(db, token) {
  const link = (db.payLinks ?? []).find((l) => l.token === token) ?? null;
  const request = link ? (db.requests.find((r) => r.id === link.requestId) ?? null) : null;
  return { link, request };
}

/**
 * Issue (or re-issue) the payment link for a request. Any earlier active link
 * is voided — the old URL stops working the moment a new one exists.
 */
export function issueJoinLink(draft, requestId, token) {
  const r = draft.requests.find((x) => x.id === requestId);
  if (!r) throw new Error('That request is no longer in the queue.');
  if (!['pending', 'link_sent'].includes(r.status)) {
    throw new Error('That request has already been handled.');
  }

  draft.payLinks ??= [];
  for (const l of draft.payLinks) {
    if (l.requestId === requestId && l.status === 'active') l.status = 'void';
  }
  draft.payLinks.push({
    id: `lnk_${token}`,
    token,
    requestId,
    amount: TIERS[r.tier].fee,
    createdOn: today(),
    status: 'active', // 'active' | 'used' | 'void'
  });
  r.status = 'link_sent';
  r.linkSentOn = today();
  return token;
}

/**
 * The member paid on the link. Records the payment ON THE REQUEST — there is
 * no member yet to attach it to; activation moves it onto the roster.
 */
export function payJoinLink(draft, token, receipt, paidOn = today()) {
  const { link, request } = joinRequestByToken(draft, token);
  if (!link || !request || link.status !== 'active') {
    throw new Error('This payment link is no longer active.');
  }
  if (request.paidOn) throw new Error('This link has already been paid.');
  if (request.status !== 'link_sent') {
    throw new Error('This payment link is no longer active.');
  }

  request.status = 'paid';
  request.paidOn = paidOn;
  request.amount = link.amount;
  request.receipt = receipt;
  return receipt;
}

/**
 * After paying, the member confirms their details and sets a password.
 * This is the moment the one-time link is spent.
 */
export function completeJoinOnboarding(draft, token, { password, ...details }) {
  const { link, request } = joinRequestByToken(draft, token);
  if (!link || !request || request.status !== 'paid' || link.status !== 'active') {
    throw new Error('This link is no longer active.');
  }
  const problem = passwordProblem(password);
  if (problem) throw new Error(problem);

  // An email is the only way back in after a forgotten password, so it has to
  // exist before the link is spent. The applicant supplied one on the join
  // form; this catches an old request filed before email was mandatory, and
  // a member correcting a typo on the way through.
  const email = normalizeEmail(details.email ?? request.email);
  if (!email) throw new Error('A valid email address is required.');

  // Only fields the member owns — tier and phone stay what was approved.
  const EDITABLE = [
    'name',
    'dob',
    'gender',
    'ground',
    'blood',
    'emName',
    'emPhone',
    'emRel',
    'tshirt',
  ];
  for (const key of EDITABLE) {
    if (details[key] !== undefined && details[key] !== '') request[key] = details[key];
  }
  request.email = email;

  request.password = password;
  request.onboarded = true;
  link.status = 'used'; // one-time: this URL is now dead
}

/**
 * The admin's final approval. Creates the member (bib allocated by the
 * caller inside the same commit), moves the payment onto the roster, and
 * only from this moment can the person sign in.
 */
export function activateJoinRequest(draft, requestId, { memberId, paymentId, bib }) {
  const r = draft.requests.find((x) => x.id === requestId);
  if (!r) throw new Error('That request is no longer in the queue.');
  if (r.status !== 'paid') {
    throw new Error('Payment has not been completed for this request yet.');
  }

  const coversTill = nextPaidTill(null, r.paidOn, r.tier);

  // Both login identities are stored one way only — E.164 for the number,
  // lowercased for the address. SPEC.md §4: normalise on write.
  const phone = normalizePhone(r.phone) ?? r.phone;
  const email = normalizeEmail(r.email);

  /*
   * One number, one member record.
   *
   * A rejoining ex-member used to get a SECOND row here, and sign-in resolved
   * their number to the old `status: 'left'` one — so they were locked out of
   * a membership they had just paid for. Reactivate the existing record
   * instead: same person, same bib (bibs are never reused — CLAUDE.md §10.7),
   * their old results and volunteer hours intact.
   */
  const existing = findMemberByPhone(draft.members, phone);
  if (existing && existing.status !== 'left') {
    throw new Error(`${existing.name} (bib ${existing.bib}) already uses that number.`);
  }
  if (existing) {
    Object.assign(existing, {
      name: r.name,
      phone,
      email: email ?? existing.email ?? null,
      dob: r.dob,
      gender: r.gender,
      ground: r.ground,
      tier: r.tier,
      blood: r.blood ?? existing.blood ?? null,
      emName: r.emName,
      emPhone: r.emPhone,
      emRel: r.emRel ?? existing.emRel ?? null,
      tshirt: r.tshirt ?? existing.tshirt ?? null,
      guardianName: r.guardianName ?? null,
      guardianPhone: r.guardianPhone ?? null,
      password: r.password ?? existing.password ?? null,
      status: 'active',
      duesPaidTill: coversTill,
      linkSent: false,
    });

    draft.payments.push({
      id: paymentId,
      memberId: existing.id,
      amount: r.amount,
      method: 'razorpay',
      date: r.paidOn,
      coversTill,
      receipt: r.receipt,
    });

    for (const l of draft.payLinks ?? []) {
      if (l.requestId === requestId && l.status === 'active') l.status = 'used';
    }

    r.status = 'approved';
    r.bib = existing.bib;
    return existing;
  }

  const member = {
    id: memberId,
    bib,
    name: r.name,
    phone,
    email,
    dob: r.dob,
    gender: r.gender,
    ground: r.ground,
    tier: r.tier,
    blood: r.blood ?? null,
    emName: r.emName,
    emPhone: r.emPhone,
    emRel: r.emRel ?? null,
    medical: null,
    tshirt: r.tshirt ?? null,
    guardianName: r.guardianName ?? null,
    guardianPhone: r.guardianPhone ?? null,
    // null when the committee activated them before they finished onboarding.
    // They recover with "Forgot password", which emails them a link to set one.
    password: r.password ?? null,
    status: 'active',
    duesPaidTill: coversTill,
    linkSent: false,
    joined: today(),
    photo: null,
  };
  draft.members.push(member);

  draft.payments.push({
    id: paymentId,
    memberId,
    amount: r.amount,
    method: 'razorpay',
    date: r.paidOn,
    coversTill,
    receipt: r.receipt,
  });

  // A paid-but-never-onboarded member may still hold an active link. It is
  // spent now — activation closes the pipeline.
  for (const l of draft.payLinks ?? []) {
    if (l.requestId === requestId && l.status === 'active') l.status = 'used';
  }

  r.status = 'approved';
  r.bib = bib;
  return member;
}
